/**
 * Auth store.
 *
 * The access token lives in the api module's closure, not here — nothing that
 * ends up in a React devtools snapshot or a state dump should hold it.
 */
import { create } from 'zustand';
import { api, setAccessToken, bootstrapSession } from '@/lib/api';
import type { User } from '@/lib/types';

interface AuthState {
  user: User | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  bootstrap: () => Promise<void>;
  clear: () => void;
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  status: 'loading',

  async login(email, password) {
    const res = await api.post<{ accessToken: string; user: User }>('/auth/login', {
      email,
      password,
    });
    setAccessToken(res.data.accessToken);
    set({ user: res.data.user, status: 'authenticated' });
  },

  async logout() {
    try {
      await api.post('/auth/logout');
    } catch {
      // Network failure must not trap the user in a session they asked to end.
    }
    setAccessToken(null);
    set({ user: null, status: 'anonymous' });
  },

  async bootstrap() {
    const session = await bootstrapSession();
    if (session) set({ user: session.user, status: 'authenticated' });
    else set({ user: null, status: 'anonymous' });
  },

  clear() {
    setAccessToken(null);
    set({ user: null, status: 'anonymous' });
  },
}));
